import React from 'react';
import { InvoiceDoc, CompanyProfileDoc } from './types';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });

interface Props {
  invoice: InvoiceDoc;
  company: CompanyProfileDoc | null;
}

const InvoicePrintable: React.FC<Props> = ({ invoice, company }) => {
  return (
    <div
      id="invoice-content"
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
    >
      {/* Company Logo and Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 items-start">
        <div>
          {company?.logo?.url && (
            <img
              src={company.logo.url}
              alt="Company Logo"
              className="h-16 mb-4 object-contain"
            />
          )}
          <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-12">From:</h3>
          <div className="space-y-1">
            <p className="font-medium text-gray-900">{company?.companyName}</p>
            <p className="text-gray-600">{company?.email}</p>
            {company?.contactNumber && <p className="text-gray-600">{company?.contactNumber}</p>}
            {company && (
              <div className="text-gray-600">
                <p>{company.address.street}</p>
                <p>{company.address.city}, {company.address.state} - {company.address.pincode}</p>
                <p>{company.address.country}</p>
              </div>
            )}
            <p className="text-gray-600">GSTIN: {company?.gstNumber}</p>
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">INVOICE</h2>
          <div className="space-y-1 mb-6">
            <p className="text-gray-600">Invoice No: {invoice.invoiceNumber}</p>
            <p className="text-gray-600">Date: {formatDate(invoice.invoiceDate)}</p>
          </div>
          <div className="pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600 mb-2">
              Total Amount
            </div>
            <div className="text-2xl font-bold text-primary-600">
              {formatCurrency(invoice.totalAmount)}
            </div>
          </div>
        </div>
      </div>

      {/* Client Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
          <div className="space-y-1">
            <p className="font-medium text-gray-900">{invoice.client.clientName}</p>
            <p className="text-gray-600">{invoice.client.email}</p>
            {invoice.client.contactNumber && <p className="text-gray-600">{invoice.client.contactNumber}</p>}
            <div className="text-gray-600">
              <p>{invoice.client.address.street}</p>
              <p>{invoice.client.address.city}, {invoice.client.address.state} - {invoice.client.address.pincode}</p>
              <p>{invoice.client.address.country}</p>
            </div>
            {invoice.client.gstin && (
              <p className="text-gray-600">GSTIN: {invoice.client.gstin}</p>
            )}
          </div>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Details:</h3>
          {company?.bankDetails?.bankName && (
            <div className="space-y-1 text-gray-600 mb-4">
              <p className="font-semibold text-gray-900 mb-2">Bank Details:</p>
              <p><strong>Bank Name:</strong> {company.bankDetails.bankName}</p>
              {company.bankDetails.branch && (
                <p><strong>Branch:</strong> {company.bankDetails.branch}</p>
              )}
              <p><strong>Account Number:</strong> {company.bankDetails.accountNumber}</p>
              {company.bankDetails.ifscCode && (
                <p><strong>IFSC Code:</strong> {company.bankDetails.ifscCode}</p>
              )}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-gray-300 space-y-1 text-gray-600">
            <p><strong>Due Date:</strong> {formatDate(invoice.dueDate)}</p>
            <p><strong>Payment Terms:</strong> {invoice.paymentTerms}</p>
          </div>
        </div>
      </div>

      {/* Invoice Items */}
      <div className="mb-8">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SAC</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoice.items.map((item, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.serialNumber}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{item.description}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.sac || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.rate)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invoice Totals */}
      <div className="flex justify-end">
        <div className="w-80">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Taxable Amount:</span>
              <span className="font-medium">{formatCurrency(invoice.taxableAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">CGST ({invoice.cgstRate}%):</span>
              <span className="font-medium">{formatCurrency(invoice.cgstAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">SGST ({invoice.sgstRate}%):</span>
              <span className="font-medium">{formatCurrency(invoice.sgstAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">IGST ({invoice.igstRate}%):</span>
              <span className="font-medium">{formatCurrency(invoice.igstAmount)}</span>
            </div>
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between">
                <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                <span className="text-lg font-bold text-primary-600">{formatCurrency(invoice.totalAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Amount in Words */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">
          <strong>Amount in Words:</strong> {invoice.totalAmountInWords}
        </p>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes:</h3>
          <p className="text-gray-600">{invoice.notes}</p>
        </div>
      )}

      {/* Terms and Conditions */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Terms & Conditions:</h3>
        <p className="text-sm text-gray-600">
          We declare that this invoice shows the actual price of the goods/services described and that all particulars are true and correct.
        </p>
      </div>

      {/* Signature */}
      <div className="mt-8 flex justify-end">
        <div className="text-center">
          {company?.digitalSignature?.url && (
            <div className="mb-4">
              <img
                src={company.digitalSignature.url}
                alt="Digital Signature"
                className="h-16 object-contain mx-auto"
              />
            </div>
          )}
          <p className="text-sm font-semibold text-gray-900">{company?.companyName || ''}</p>
          <p className="text-xs text-gray-500 mt-1">
            Authorized Signatory
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintable;
